import agentMutations from '@/api/mutitations/agent';
import agentQuery from '@/api/queries/agent';
import companyQuery from '@/api/queries/company';
import productQuery from '@/api/queries/product';
import prepareTranslate from '@/helpers/dictionary';
import { ACCORD_TYPE, PROCESS_NOTIFICATION_SCREENS, PRODUCT_ENTITY } from '@/helpers/enums';
import {
  Autocomplete,
  Center,
  Loader,
  LoadingOverlay,
  Text,
} from '@mantine/core';
import { useDebouncedCallback, useDisclosure } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import CreateAgentClientInProcess from '../CreateAgentClientInProcess';
import CreateAgentAgreementInProcess from '../CreateAgentAgreementInProcess';
import CreateAgentAlternativeFundInProcess from '../CreateAgentAlternativeFundInProcess';

function CreateClientOrAgreement(props) {
  const {
    form,
    currentIdentifier,
    createdAgent,
    clientOrAgreement,
  } = props;

  const {
    year,
    month,
    identifierNumber,
    company: identifierCompany,
    product: identifierProduct,
  } = currentIdentifier;

  const t = prepareTranslate();

  const [autoCompleteValue, setAutoCompleteValue] = useState(createdAgent?.string);
  const [pickedAgentId, setPickedAgentId] = useState(createdAgent?.id);
  const [autoCompleteData, setAutoCompleteData] = useState([]);

  const { data: products } = productQuery.useGetAll();
  const { data: companies } = companyQuery.useGetAll();
  const { data: agentData, status, isFetching } = agentQuery.useGetAgentById({ id: pickedAgentId });
  const { mutateAsync: agentMutate } = agentMutations.useGetAgents();

  const [autoSelectLoading, { open: autoSelectOpen, close: autoSelectClose }] = useDisclosure();

  const toOptions = (items = []) => items.map((product) => ({
    label: product.hebrew, value: product._id,
  }));

  const filterByEntity = (entity) => products?.filter((p) => p.applicableTo.includes(entity)) ?? [];

  const clientProductsOptions = toOptions(filterByEntity(PRODUCT_ENTITY.client));
  const agreementProductsOptions = toOptions(filterByEntity(PRODUCT_ENTITY.agreement));
  const alternativeFundProductsOptions = toOptions(filterByEntity(PRODUCT_ENTITY.alternativeFund));

  const companiesOptions = companies?.map((company) => ({
    label: company.hebrew,
    value: company._id,
  }));

  const autoCompleteQuery = useDebouncedCallback(async (val) => {
    autoSelectOpen();

    if (val === '') {
      setAutoCompleteData([]);
    } else {
      const start = 1;
      const end = 100;
      const query = { 'personalDetails.fullName': val };

      try {
        const { data: agentsData } = await agentMutate({ query, start, end });

        if (agentsData.length === 0) {
          setAutoCompleteData([]);
        } else {
          setAutoCompleteData(agentsData.map((agent) => ({ label: `${agent.personalDetails.fullName} (${agent.personalDetails.uid})`, value: agent._id })));
        }
      } catch {
        notifications.show({
          title: t('pages.reports.notification.error.title'),
          message: t('pages.reports.notification.error.message'),
          color: 'red',
        });
      }
    }

    autoSelectClose();
  }, 500);

  const handleAutocompleteChange = (val) => {
    if (pickedAgentId) {
      setAutoCompleteValue('');
      setAutoCompleteData([]);
      setPickedAgentId(null);
    } else {
      const hasAgent = autoCompleteData.find((agent) => agent.label === val);
      setAutoCompleteValue(val);

      if (!hasAgent) {
        autoCompleteQuery(val);
      } else {
        setAutoCompleteData([]);
      }
    }
  };

  const handleOptionSubmit = (val) => {
    setPickedAgentId(val);
  };

  useEffect(() => {
    const agreementsSanitizedData = agentData?.agreements?.map((item) => {
      const itemCopy = item;
      itemCopy.depositFrom = item.depositFrom ? new Date(item.depositFrom) : null;

      itemCopy.companyId = item.companyId?._id;
      itemCopy.branchId = item.branchId.map((branch) => branch?._id);

      return itemCopy;
    });

    const clientsSanitizedData = agentData?.clients.map((item) => {
      const itemCopy = item;
      itemCopy.companyId = item.companyId?._id;
      itemCopy.branchId = item.branchId?._id;
      return itemCopy;
    });

    const alternativeFundsSanitizedData = agentData?.alternativeFunds?.map((item) => {
      const itemCopy = item;
      itemCopy.companyId = item.companyId?._id;
      itemCopy.branchId = item.branchId?._id;
      return itemCopy;
    });

    form.setValues({
      id: pickedAgentId,
      personalDetails: agentData?.personalDetails,
      agreements: agreementsSanitizedData,
      status: agentData?.status,
      clients: clientsSanitizedData,
      alternativeFunds: alternativeFundsSanitizedData || [],
      agentType: agentData?.agentType,
      employeeType: agentData?.employeeType,
    });

    if (clientOrAgreement === PROCESS_NOTIFICATION_SCREENS.createAgreement) {
      // Add empty agreement
      form.insertListItem('agreements', {
        accordType: ACCORD_TYPE[0].value,
        agentNumber: identifierNumber,
        depositFrom: new Date(year, month - 1),
        accumulationFrom: new Date(year, month - 1),
        companyId: identifierCompany.id,
        branchId: identifierProduct.map((product) => product.id),
        agentFeeAnnualDeposit: [{ from: 0, to: null, rate: 0 }],
        agentFeeAnnualAccumulation: [{ from: 0, to: null, rate: 0 }],
        files: [],
      });
    } else if (clientOrAgreement === PROCESS_NOTIFICATION_SCREENS.createClient) {
      form.insertListItem('clients', {
        accordType: ACCORD_TYPE[0].value,
        clientNumber: identifierNumber,
        companyId: identifierCompany.id,
        branchId: identifierProduct[0].id,
      });
    } else if (clientOrAgreement === PROCESS_NOTIFICATION_SCREENS.createAlternativeFund) {
      form.insertListItem('alternativeFunds', {
        accordType: ACCORD_TYPE[0].value,
        alternativeFundNumber: identifierNumber,
        companyId: identifierCompany.id,
        branchId: identifierProduct[0].id,
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agentData, status]);

  const clientTerm = (clientOrAgreement === PROCESS_NOTIFICATION_SCREENS.createClient);

  const agreementTerm = (clientOrAgreement === PROCESS_NOTIFICATION_SCREENS.createAgreement);

  const alternativeFundTerm = (
    clientOrAgreement === PROCESS_NOTIFICATION_SCREENS.createAlternativeFund
  );

  let clientOrAgreementOrAlternativeFundTitle;
  if (clientOrAgreement === PROCESS_NOTIFICATION_SCREENS.createClient) {
    clientOrAgreementOrAlternativeFundTitle = t('components.processNotification.popUp.createClient');
  } else if (clientOrAgreement === PROCESS_NOTIFICATION_SCREENS.createAgreement) {
    clientOrAgreementOrAlternativeFundTitle = t('components.processNotification.popUp.createAgreement');
  } else {
    clientOrAgreementOrAlternativeFundTitle = t('components.processNotification.popUp.createAlternativeFund');
  }
  const disabled = !pickedAgentId;

  return (
    <>
    <Center>
        <Text
          size='xl'
          fw='bold'
        >
          {clientOrAgreementOrAlternativeFundTitle}
        </Text>
        </Center>
      <LoadingOverlay visible={isFetching} />
      <Center mb='lg'>
        <Autocomplete
          label={t('pages.reports.form.agent')}
          rightSection={autoSelectLoading ? <Loader size="1rem" /> : null}
          data={autoCompleteData}
          limit="10"
          key={form.key('agent')}
          {...form.getInputProps('agent')}
          value={autoCompleteValue}
          onChange={handleAutocompleteChange}
          onOptionSubmit={handleOptionSubmit}
          w='300px'
        />
      </Center>
      {
        clientTerm && <CreateAgentClientInProcess
          form={form}
          productsOptions={clientProductsOptions}
          companiesOptions={companiesOptions}
          agentData={agentData}
          pickedAgentId={pickedAgentId}
          currentIdentifier={currentIdentifier}
          disabled= {disabled}
        />
      }
      {
        agreementTerm && <CreateAgentAgreementInProcess
          form={form}
          productsOptions={agreementProductsOptions}
          companiesOptions={companiesOptions}
          agentData={agentData}
          currentIdentifier={currentIdentifier}
          disabled= {disabled}
        />
      }
      {
        alternativeFundTerm && <CreateAgentAlternativeFundInProcess
          form={form}
          productsOptions={alternativeFundProductsOptions}
          companiesOptions={companiesOptions}
          agentData={agentData}
          currentIdentifier={currentIdentifier}
          disabled={disabled}
        />
      }
    </>
  );
}

CreateClientOrAgreement.propTypes = {
  form: PropTypes.shape({
    setValues: PropTypes.func.isRequired,
    insertListItem: PropTypes.func.isRequired,
    getValues: PropTypes.func.isRequired,
    key: PropTypes.func.isRequired,
    getInputProps: PropTypes.func.isRequired,
  }).isRequired,
  clientOrAgreement: PropTypes.number,
  currentIdentifier: PropTypes.shape({
    year: PropTypes.string.isRequired,
    month: PropTypes.string.isRequired,
    identifierNumber: PropTypes.string.isRequired,
    company: PropTypes.shape({
      id: PropTypes.string.isRequired,
    }).isRequired,
    product: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.string.isRequired,
    })).isRequired,
  }).isRequired,
  createdAgent: PropTypes.shape({
    id: PropTypes.string.isRequired,
    string: PropTypes.string.isRequired,
  }),
};

export default CreateClientOrAgreement;
